import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, Loader2, Leaf, Trash2, Plus, MessageSquare,
  CheckCircle2, Circle, Pencil, X, Check, Copy,
  PanelRightOpen, PanelRightClose, // For logs panel toggle (currently hidden)
  Share2,
} from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MarketplaceShell from './MarketplaceShell';
import { startChat, consumeChatQuery } from './marketplaceApi';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

const NATURAL_ORGANIC_ID = Number(import.meta.env.VITE_NATURAL_ORGANIC_SEGMENT_ID || 222);
const FLAVCITY_ID = Number(import.meta.env.VITE_FLAVCITY_SEGMENT_ID || 385);
const KNF_SURVEY_ID = Number(import.meta.env.VITE_KNF_SURVEY_SEGMENT_ID || 390);
const SMARTYPANTS_RAW_ID = Number(import.meta.env.VITE_SMARTYPANTS_RAW_SEGMENT_ID || 391);
const SMARTYPANTS_SYNTH_ID = Number(import.meta.env.VITE_SMARTYPANTS_SYNTH_SEGMENT_ID || 392);
const SMARTYPANTS_DT_ID = Number(import.meta.env.VITE_SMARTYPANTS_DT_SEGMENT_ID || 393);
const COOKIE_CONSUMER_ID = Number(import.meta.env.VITE_COOKIE_CONSUMER_SEGMENT_ID || 394);
const KNF_DEEP_SURVEY_ID = Number(import.meta.env.VITE_KNF_DEEP_SURVEY_SEGMENT_ID || 411);
const DOGS_AUDIENCE_ID = Number(import.meta.env.VITE_DOGS_AUDIENCE_SEGMENT_ID || 434);
const CATS_AUDIENCE_ID = Number(import.meta.env.VITE_CATS_AUDIENCE_SEGMENT_ID || 435);
const KNF_DEEP_OLD_JOINT_ID = Number(import.meta.env.VITE_KNF_DEEP_OLD_JOINT_SEGMENT_ID || 446);
const JERKY_ID = Number(import.meta.env.VITE_JERKY_SEGMENT_ID || 449);
// Synthetic scale-test segment: 1000 (largest) personas in a single segment.
const MOCK_CHAT_1000_ID = Number(import.meta.env.VITE_MOCK_CHAT_1000_SEGMENT_ID || 470);

const AVAILABLE_SEGMENTS = [
  { id: NATURAL_ORGANIC_ID, name: 'Natural Organic Shoppers', icon: Leaf },
  { id: FLAVCITY_ID, name: 'FlavCity Protein Consumers', icon: Leaf },
  { id: KNF_SURVEY_ID, name: 'Kettle & Fire Survey', icon: Leaf, adminOnly: true },
  { id: SMARTYPANTS_RAW_ID, name: 'SmartyPants', icon: Leaf, adminOnly: true },
  // { id: SMARTYPANTS_SYNTH_ID, name: 'SmartyPants Synthesized Digital Twins', icon: Leaf, adminOnly: true },
  // { id: SMARTYPANTS_DT_ID, name: 'SmartyPants Digital Twins', icon: Leaf, adminOnly: true },
  { id: COOKIE_CONSUMER_ID, name: 'Cookie Consumers', icon: Leaf, adminOnly: true },
  { id: KNF_DEEP_SURVEY_ID, name: 'K&F Deep Survey', icon: Leaf, adminOnly: true },
  { id: DOGS_AUDIENCE_ID, name: 'Dog Owners (Full)', icon: Leaf, adminOnly: true },
  { id: CATS_AUDIENCE_ID, name: 'Cat Owners (Full)', icon: Leaf, adminOnly: true },
  { id: KNF_DEEP_OLD_JOINT_ID, name: 'Kettle & Fire Deep Old Joint Consumers', icon: Leaf, adminOnly: true },
  { id: JERKY_ID, name: 'Jerky & Meat-Snack Consumers Deep Survey', icon: Leaf, adminOnly: true },
  { id: MOCK_CHAT_1000_ID, name: 'MOCK Chat Test (1000 personas)', icon: Leaf, adminOnly: true },
];

const DEFAULT_SEGMENT_ID = NATURAL_ORGANIC_ID;

// ── Types ────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
  progress_snapshot?: StructuredProgress; // Saved progress for display in chat history
}

interface ChatSession {
  id: number;
  title: string | null;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

// Progress tracking types
interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  group?: 'planning' | 'output';  // Group for visual organization
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages: StageInfo[];
  plan_steps?: PlanStepProgress[];
  plan_version: number;
  message: string;
}

interface TaskStatusResp {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  progress?: number;
  message?: string;
  structured_progress?: StructuredProgress;
  result?: {
    response: string;
    format: string;
    personas_cited: string[];
  };
  error?: string;
}

interface LogEntry {
  id: number;
  text: string;
  agent: string;
  label: string;
  isHeader: boolean;
  isSeparator: boolean;
}

// ── Time-based progress: NEVER goes backwards ──

// Calculate progress with configurable duration
// Fast tasks (analyzing, executing): 60 seconds to ~80%
// Slow tasks (planning, output): 4 minutes (240 seconds) to ~95%
const calculateProgress = (
  startTime: number,
  maxProgress: number = 80,
  durationMs: number = 60000
): number => {
  const elapsed = Date.now() - startTime;

  // Use easing function for natural feel
  const t = Math.min(elapsed / durationMs, 1);

  // Ease-out curve: fast start, slows down as approaching max
  // Formula: 1 - (1-t)^2 gives nice ease-out
  const eased = 1 - Math.pow(1 - t, 2);

  return eased * maxProgress;
};

// Duration constants
const FAST_DURATION = 60000;   // 60 seconds for fast stages
const SLOW_DURATION = 240000;  // 4 minutes for planning/output

// ── Time-based progress bar component ──
const TimeBasedProgressBar: React.FC<{
  taskId: string;
  startTime: number | undefined;
  isCompleted: boolean;
}> = ({ taskId, startTime, isCompleted }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const maxReachedRef = useRef(0); // CRITICAL: Track max progress reached - never go below this
  const effectiveStartTimeRef = useRef<number | null>(null); // Capture start time on first active render

  useEffect(() => {
    if (isCompleted) {
      // Animate to 100% smoothly
      const animateTo100 = () => {
        setDisplayProgress(prev => {
          if (prev >= 99.5) {
            maxReachedRef.current = 100;
            return 100;
          }
          const next = prev + (100 - prev) * 0.2;
          maxReachedRef.current = Math.max(maxReachedRef.current, next);
          return next;
        });
      };
      const interval = setInterval(animateTo100, 50);
      setTimeout(() => {
        clearInterval(interval);
        setDisplayProgress(100);
        maxReachedRef.current = 100;
      }, 500);
      return () => clearInterval(interval);
    }

    // Use provided startTime OR capture current time on first active render
    // This fixes the bug where startTime is undefined due to React async state updates
    if (!effectiveStartTimeRef.current) {
      effectiveStartTimeRef.current = startTime || Date.now();
    }
    const effectiveStartTime = effectiveStartTimeRef.current;

    // Use slow duration (4 min) for final_output, fast (60s) for other tasks
    const isSlowTask = taskId === 'final_output';
    const duration = isSlowTask ? SLOW_DURATION : FAST_DURATION;
    const hashCode = taskId.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    const maxProgress = isSlowTask ? 90 + (Math.abs(hashCode) % 8) : 75 + (Math.abs(hashCode) % 10); // 90-98% or 75-85%

    const interval = setInterval(() => {
      const targetProgress = calculateProgress(effectiveStartTime, maxProgress, duration);

      // CRITICAL: Never go below the maximum we've reached
      const newProgress = Math.max(maxReachedRef.current, targetProgress);
      maxReachedRef.current = newProgress;

      // Smooth transition towards target
      setDisplayProgress(prev => {
        const diff = newProgress - prev;
        if (Math.abs(diff) < 0.3) return newProgress;
        // Move 20% of the way each tick for smooth animation
        return prev + diff * 0.2;
      });
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [startTime, isCompleted, taskId]);

  const showProgress = Math.round(displayProgress);

  return (
    <div className="mt-2">
      <div className="h-1.5 bg-[#07E5D1]/20 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#07E5D1] rounded-full transition-all duration-300 ease-out"
          style={{ width: `${showProgress}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[10px] text-[#07E5D1] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {showProgress < 30 ? 'Starting...' : showProgress < 60 ? 'Processing...' : 'Almost there...'}
        </p>
        <span className="text-[10px] text-gray-400">{showProgress}%</span>
      </div>
    </div>
  );
};

// ── Collapsible Progress Accordion for chat history ──
// Only shows the execution plan steps (the actual work done), not static stages
const ProgressAccordion: React.FC<{ progress: StructuredProgress }> = ({ progress }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const planSteps = progress.plan_steps || [];

  // Don't render if no plan steps
  if (planSteps.length === 0) return null;

  return (
    <div className="mt-3 border border-black/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2.5 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
          <span className="text-xs font-medium text-gray-700">
            Completed {planSteps.length} tasks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">View execution plan</span>
          {isExpanded ? (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-white border-t border-black/10">
          <ul className="space-y-1.5">
            {planSteps.map((step, idx) => (
              <li key={`${step.id}-${idx}`} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#07E5D1] flex-shrink-0 mt-0.5" />
                <span className="text-xs text-gray-500 line-through">{step.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AGENT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  ORCHESTRATOR:      { bg: 'bg-[#E8FCFA]',  text: 'text-[#0B7C74]',   dot: 'bg-[#07E5D1]' },
  QUERY_ANALYZER:    { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500' },
  PLANNING_AGENT:    { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  EXECUTION_AGENT:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  OUTPUT_ANALYST:    { bg: 'bg-pink-50',    text: 'text-pink-700',    dot: 'bg-pink-500' },
  GLOBAL:            { bg: 'bg-gray-50',    text: 'text-gray-600',    dot: 'bg-gray-400' },
};

function getAgentColor(agent: string) {
  return AGENT_COLORS[agent] || { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' };
}

let _logId = 0;
function parseLine(raw: string): LogEntry {
  _logId += 1;
  const trimmed = raw.trim();
  if (/^[═─]{10,}/.test(trimmed)) {
    return { id: _logId, text: '', agent: '', label: '', isHeader: false, isSeparator: true };
  }
  const headerMatch = trimmed.match(/^\[(\d{2}:\d{2}:\d{2}\.\d{3})\]\s+([A-Z_]+)\s+\|\s+(.+)$/);
  if (headerMatch) {
    const [, time, agent, label] = headerMatch;
    return { id: _logId, text: `${time}`, agent, label, isHeader: true, isSeparator: false };
  }
  return { id: _logId, text: trimmed, agent: '', label: '', isHeader: false, isSeparator: false };
}

// ── Markdown components ──────────────────────────────────────────────────

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} className="text-[#07E5D1] hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

// ── Main Component ───────────────────────────────────────────────────────

const MarketplaceTwinChat: React.FC = () => {
  const navigate = useNavigate();
  const { studyId: studyIdParam } = useParams<{ studyId?: string }>();
  const studyId = Number(studyIdParam);

  // The audience is the marketplace study's selected profiles (an in-memory
  // persona subset), bootstrapped server-side via /chat/start. No segment dropdown.
  const [selectedSegmentId] = useState<number>(0); // retained for unused legacy refs
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [queriesAllowed, setQueriesAllowed] = useState<number>(3);
  const [startError, setStartError] = useState<string | null>(null);

  // // Context filtering toggle (admin only) - undefined means use server config default
  // const [contextFilteringEnabled, setContextFilteringEnabled] = useState<boolean | undefined>(undefined);

  // Session state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  // Input / loading
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Analyzing your question...');
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  const [stepStartTimes, setStepStartTimes] = useState<Record<string, number>>({});
  const [stageStartTimes, setStageStartTimes] = useState<Record<string, number>>({});
  const [lastProgressSnapshot, setLastProgressSnapshot] = useState<StructuredProgress | null>(null);
  const progressSnapshotRef = useRef<StructuredProgress | null>(null); // Sync ref for immediate access in SUCCESS handler

  // Animated dots for loading states (cycles: "" -> "." -> ".." -> "..." -> "")
  const [animatedDots, setAnimatedDots] = useState('');
  useEffect(() => {
    if (!isLoading) { setAnimatedDots(''); return; }
    const interval = setInterval(() => {
      setAnimatedDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, [isLoading]);

  const [showLogs, setShowLogs] = useState(true);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const logOffsetRef = useRef(0);
  const logFileRef = useRef<string | null>(null);
  const logScrollRef = useRef<HTMLDivElement>(null);
  const logPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refs
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Scroll chat to bottom
  const scrollToBottom = useCallback(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<number>(2000);

  // Rename session state
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete confirmation state
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<{
    share_token: string;
    share_url: string;
    message_count: number;
    view_count: number;
    fork_count: number;
  } | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);

  // Focus input
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);


  // ── Session CRUD ─────────────────────────────────────────────────

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions?segment_id=${selectedSegmentId}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, [selectedSegmentId]);


  // Check if a session has an active task and resume polling if so
  const checkSessionStatus = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${sessionId}/status`, { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data; // { has_active_task: boolean, task_id?: string }
    } catch {
      return null;
    }
  }, []);

  // Store refs to polling functions (defined later) for use in effects
  const pollTaskStatusRef = useRef<((taskId: string) => Promise<void>) | null>(null);
  const startLogPollingRef = useRef<((sessionId: number) => void) | null>(null);

  // Stop any active polling (used when switching sessions)
  const stopAllPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
    currentTaskIdRef.current = null;
    setIsLoading(false);
    setStructuredProgress(null);
    setProgressMessage('');
  }, []);

  const createSession = useCallback(async () => {
    stopAllPolling();

    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_id: selectedSegmentId }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setActiveSessionId(data.id);
      setMessages([]);
      await fetchSessions();
    } catch { /* ignore */ }
  }, [fetchSessions, stopAllPolling, selectedSegmentId]);

  const loadSession = useCallback(async (id: number) => {
    // Stop any existing polling before switching
    stopAllPolling();
    setIsSessionLoading(true);

    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, { credentials: 'include' });
      if (!res.ok) {
        setIsSessionLoading(false);
        return;
      }
      const data = await res.json();
      setActiveSessionId(id);
      // URL will be updated by the effect that watches activeSessionId
      setMessages(
        (data.messages || []).map((m: any) => ({
          role: m.role,
          content: m.content,
          personas_cited: m.metadata?.personas_cited,
          format: m.metadata?.format,
          progress_snapshot: m.metadata?.progress_snapshot,
        }))
      );

      // Check if there's an active task and resume polling
      const status = await checkSessionStatus(id);
      if (status?.has_active_task && status.task_id) {
        setIsLoading(true);
        setProgressMessage('Resuming...');
        setStructuredProgress({
          current_stage: 'resuming',
          message: 'Resuming...',
          plan_steps: [],
        });
        currentTaskIdRef.current = status.task_id;
        if (pollRef.current) clearInterval(pollRef.current);
        const taskId = status.task_id;
        pollRef.current = setInterval(() => {
          pollTaskStatusRef.current?.(taskId);
        }, 1000);
        pollTaskStatusRef.current?.(taskId);
        startLogPollingRef.current?.(id);
      }
    } catch { /* ignore */ }
    setIsSessionLoading(false);
  }, [stopAllPolling, checkSessionStatus]);

  // Track if we've initialized from URL to prevent loops
  const hasInitializedFromUrlRef = useRef(false);

  // ONE-TIME: bootstrap the chat session from the marketplace study. /chat/start
  // resolves the study's selected profiles into an in-memory persona audience,
  // creates the hc-v2 session bound to it, and returns the session id + budget.
  useEffect(() => {
    if (hasInitializedFromUrlRef.current || !studyId) return;
    hasInitializedFromUrlRef.current = true;
    (async () => {
      setIsSessionLoading(true);
      try {
        const s = await startChat(studyId);
        setQueriesRemaining(s.queries_remaining);
        setQueriesAllowed(s.queries_allowed);
        setActiveSessionId(s.session_id);
        const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${s.session_id}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setMessages((data.messages || []).map((m: any) => ({
            role: m.role, content: m.content,
            personas_cited: m.metadata?.personas_cited,
            format: m.metadata?.format,
            progress_snapshot: m.metadata?.progress_snapshot,
          })));
        }

        // If a query is still running on this session (the user navigated away and
        // came back), resume the live progress so they see the loading indicator
        // and the answer lands here — not just their own question.
        const status = await checkSessionStatus(s.session_id);
        if (status?.has_active_task && status.task_id) {
          const taskId = status.task_id;
          setIsLoading(true);
          setProgressMessage('Resuming…');
          setStructuredProgress({ current_stage: 'resuming', message: 'Resuming…', plan_steps: [] });
          currentTaskIdRef.current = taskId;
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = setInterval(() => pollTaskStatusRef.current?.(taskId), 1000);
          pollTaskStatusRef.current?.(taskId);
          startLogPollingRef.current?.(s.session_id);
        }
      } catch (e) {
        setStartError(e instanceof Error ? e.message : 'Could not start chat');
      }
      setIsSessionLoading(false);
    })();
  }, [studyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteSession = useCallback(async (id: number) => {
    try {
      await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (activeSessionId === id) {
        stopAllPolling();
        setActiveSessionId(null);
        setMessages([]);
        navigate('/ask-natural-organic', { replace: true });
      }
      await fetchSessions();
    } catch { /* ignore */ }
  }, [activeSessionId, fetchSessions, navigate, stopAllPolling]);

  // ── Rename session handlers ──
  const handleStartRename = useCallback((session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.title || `Chat ${session.id}`);
  }, []);

  const handleSaveRename = useCallback(async () => {
    if (!editingSessionId || !editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    setIsRenaming(true);
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${editingSessionId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      if (res.ok) {
        await fetchSessions();
      }
    } catch { /* ignore */ }
    setIsRenaming(false);
    setEditingSessionId(null);
  }, [editingSessionId, editingTitle, fetchSessions]);

  const handleCancelRename = useCallback(() => {
    setEditingSessionId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);

  // ── Delete confirmation handlers ──
  const handleDeleteClick = useCallback((id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingSessionId(id);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deletingSessionId) {
      await deleteSession(deletingSessionId);
      setDeletingSessionId(null);
    }
  }, [deletingSessionId, deleteSession]);

  const handleCancelDelete = useCallback(() => {
    setDeletingSessionId(null);
  }, []);

  // ── Share Functions ─────────────────────────────────────────────────

  const fetchExistingShare = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/share`,
        { credentials: 'include' }
      );
      if (!res.ok) return null;
      const data = await res.json();
      if (data.has_share) {
        return {
          share_token: data.share_token,
          share_url: data.share_url,
          message_count: data.message_count,
          view_count: data.view_count || 0,
          fork_count: data.fork_count || 0,
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const handleShareClick = useCallback(async () => {
    if (!activeSessionId || messages.length === 0) return;

    setIsCreatingShare(true);
    try {
      // First check if there's an existing share
      const existing = await fetchExistingShare(activeSessionId);
      if (existing) {
        setShareData(existing);
        setShowShareModal(true);
        setIsCreatingShare(false);
        return;
      }

      // Create a new share
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${activeSessionId}/share`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ allow_fork: true }),
        }
      );

      if (!res.ok) {
        console.error('Failed to create share link');
        return;
      }

      const data = await res.json();
      setShareData({
        share_token: data.share_token,
        share_url: data.share_url,
        message_count: data.message_count,
        view_count: 0,
        fork_count: 0,
      });
      setShowShareModal(true);
    } catch (err) {
      console.error('Error creating share:', err);
    } finally {
      setIsCreatingShare(false);
    }
  }, [activeSessionId, messages.length, fetchExistingShare]);

  const handleRevokeShare = useCallback(async () => {
    if (!activeSessionId || !shareData) return;

    setIsRevokingShare(true);
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${activeSessionId}/share/${shareData.share_token}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (res.ok) {
        setShareData(null);
        setShowShareModal(false);
      }
    } catch (err) {
      console.error('Error revoking share:', err);
    } finally {
      setIsRevokingShare(false);
    }
  }, [activeSessionId, shareData]);

  // Clear share data when session changes
  useEffect(() => {
    setShareData(null);
    setShowShareModal(false);
  }, [activeSessionId]);

  const pollLogs = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/logs?offset=${logOffsetRef.current}`,
        { credentials: 'include' },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.file && logFileRef.current && data.file !== logFileRef.current) {
        setLogEntries([]);
        _logId = 0;
        logOffsetRef.current = 0;
        logFileRef.current = data.file;
        return;
      }
      logFileRef.current = data.file ?? null;
      if (data.lines && data.lines.length > 0) {
        const newEntries = data.lines.map((ln: string) => parseLine(ln));
        setLogEntries(prev => [...prev, ...newEntries]);
      }
      logOffsetRef.current = data.offset;
    } catch {}
  }, []);

  const startLogPolling = useCallback((sessionId: number) => {
    if (logPollRef.current) clearInterval(logPollRef.current);
    setLogEntries([]);
    _logId = 0;
    logOffsetRef.current = 0;
    logFileRef.current = null;
    logPollRef.current = setInterval(() => pollLogs(sessionId), 1500);
    setTimeout(() => pollLogs(sessionId), 800);
  }, [pollLogs]);

  const stopLogPolling = useCallback(() => {
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
  }, []);

  // ── Task status polling ──────────────────────────────────────────

  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/status/${taskId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('status poll failed');
      const status: TaskStatusResp = await res.json();

      if (status.state === 'PENDING' || status.state === 'PROGRESS') {
        setProgressMessage(status.message || 'Processing...');
        if (status.structured_progress) {
          // Track when steps start running
          const newStepStartTimes: Record<string, number> = { ...stepStartTimes };
          status.structured_progress.plan_steps?.forEach(step => {
            if (step.status === 'running' && !newStepStartTimes[step.id]) {
              newStepStartTimes[step.id] = Date.now();
            }
          });
          // Also track when the output stage starts
          const outputStage = status.structured_progress.stages.find(s => s.stage === 'output');
          if (outputStage?.status === 'active' && !newStepStartTimes['final_output']) {
            newStepStartTimes['final_output'] = Date.now();
          }
          setStepStartTimes(newStepStartTimes);

          // Track when stages become active
          const newStageStartTimes: Record<string, number> = { ...stageStartTimes };
          status.structured_progress.stages.forEach(stage => {
            if (stage.status === 'active' && !newStageStartTimes[stage.stage]) {
              newStageStartTimes[stage.stage] = Date.now();
            }
          });
          setStageStartTimes(newStageStartTimes);

          setStructuredProgress(status.structured_progress);
          setLastProgressSnapshot(status.structured_progress);
          progressSnapshotRef.current = status.structured_progress; // Sync update for immediate access in SUCCESS handler
        }
      } else if (status.state === 'SUCCESS' && status.result) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          currentTaskIdRef.current = null;
        }
        stopLogPolling();
        // Create final progress snapshot with all steps completed (including final output step)
        // Use ref for immediate access - not subject to React state timing issues
        const snapshotToUse = progressSnapshotRef.current || lastProgressSnapshot;
        const finalSnapshot: StructuredProgress | undefined = snapshotToUse ? {
          ...snapshotToUse,
          stages: snapshotToUse.stages.map(s => ({ ...s, status: 'completed' as const })),
          plan_steps: [
            ...(snapshotToUse.plan_steps?.map(s => ({ ...s, status: 'completed' as const })) || []),
            { id: 'final_output', label: 'Preparing your answer', status: 'completed' as const }
          ],
        } : undefined;

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: status.result!.response,
          personas_cited: status.result!.personas_cited,
          format: status.result!.format,
          progress_snapshot: finalSnapshot,
        }]);
        setTimeout(scrollToBottom, 50); // Scroll to show new response
        setIsLoading(false);
        setStructuredProgress(null);
        setStepStartTimes({});
        setStageStartTimes({});
        setLastProgressSnapshot(null);
        progressSnapshotRef.current = null; // Clear the ref too
        await fetchSessions();
      } else if (status.state === 'FAILURE') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          currentTaskIdRef.current = null;
        }
        stopLogPolling();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`,
        }]);
        setTimeout(scrollToBottom, 50); // Scroll to show error message
        setIsLoading(false);
        setStructuredProgress(null);
        setStepStartTimes({});
        setStageStartTimes({});
        setLastProgressSnapshot(null);
        progressSnapshotRef.current = null; // Clear the ref too
      }
    } catch {
      // ignore transient errors
    }
  }, [activeSessionId, fetchSessions, stepStartTimes, stageStartTimes, lastProgressSnapshot, stopLogPolling]);

  // Set refs for polling functions (used by effects that run before these are defined)
  useEffect(() => {
    pollTaskStatusRef.current = pollTaskStatus;
    startLogPollingRef.current = startLogPolling;
  }, [pollTaskStatus, startLogPolling]);

  // ── Send message ─────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading || queriesRemaining <= 0) return;
    const sessionId = activeSessionId;
    if (!sessionId) return; // bootstrapped on mount via /chat/start

    // Spend one query against the server-enforced budget (403 when out).
    try {
      const consumed = await consumeChatQuery(studyId, input.trim(), sessionId);
      setQueriesRemaining(consumed.queries_remaining);
    } catch (e) {
      setStartError(e instanceof Error ? e.message : 'No chat queries left.');
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    // Scroll to bottom after adding user message
    setTimeout(scrollToBottom, 50);
    setIsLoading(true);
    setProgressMessage('Sending query...');
    // Initialize with a minimal structured progress so the thinking bubble shows immediately
    setStructuredProgress({
      current_stage: 'sending',
      message: 'Sending query...',
      plan_steps: [],
    });
    setStepStartTimes({});
    setStageStartTimes({ sending: Date.now() }); // Start tracking sending stage immediately
    progressSnapshotRef.current = null; // Clear any previous snapshot

    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/chat`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: input.trim(),
            context_filtering_enabled: true,
          }),
        },
      );

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) { navigate('/auth'); return; }
        throw new Error('Failed to send message');
      }

      const data = await res.json();
      const taskId = data.task_id;
      if (!taskId) throw new Error('No task ID received');

      // Poll every 1000ms for faster status updates (was 2000ms)
      currentTaskIdRef.current = taskId;
      pollRef.current = setInterval(() => pollTaskStatus(taskId), 1000);
      pollTaskStatus(taskId);
      startLogPolling(sessionId!);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
      setIsLoading(false);
      setStructuredProgress(null);
      setStageStartTimes({});
      setStepStartTimes({});
      progressSnapshotRef.current = null; // Clear the ref
    }
  }, [input, isLoading, activeSessionId, navigate, pollTaskStatus, startLogPolling, queriesRemaining, studyId]);

  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  const handleCopyMessage = useCallback(async (content: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Render ───────────────────────────────────────────────────────

  if (startError && !activeSessionId) {
    return (
      <MarketplaceShell maxWidth="max-w-3xl">
        <div className="py-16 text-center text-sm text-[#EF4444]">{startError}</div>
      </MarketplaceShell>
    );
  }

  return (
    <MarketplaceShell maxWidth="max-w-5xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-[22px] font-medium text-[#111]">Chat with your Twins</h1>
          <p className="text-[13px] text-[#6b6b6b]">Always-on focus group</p>
        </div>
        <span className="text-[13px] font-medium text-[#111]">
          {queriesRemaining} of {queriesAllowed} queries left
        </span>
      </div>
      <div className="flex border border-black/10 rounded-xl bg-white overflow-hidden" style={{ height: '72vh' }}>
        <section className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div ref={chatScrollRef} className="flex-1 overflow-y-auto">
            <div className="max-w-[960px] mx-auto px-6 py-6">
              {/* Session loading state */}
              {isSessionLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
                    <p className="text-sm text-[#6B7280]">Loading conversation...</p>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isSessionLoading && messages.length === 0 && !isLoading && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-5">
                    <Leaf className="w-8 h-8" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">Ask your Twins</h2>
                  <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-6">
                    Ask any question and get instant insights from your always-on focus group.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                {([
                  'What drives breakfast purchase decisions?',
                  'How do shoppers choose between breakfast brands?',
                  'What matters most for morning routines?',
                ]).map((s, i) => (
                  <button
                    key={i}
                        onClick={() => setInput(s)}
                        className="px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-xl text-xs text-[#6B7280] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors"
                  >
                        {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
              {!isSessionLoading && (
              <div className="space-y-5">
            {messages.map((msg, idx) => (
              <React.Fragment key={idx}>
                {/* Progress accordion shown BEFORE assistant message (between query and response) */}
                {msg.role === 'assistant' && msg.progress_snapshot && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl w-full">
                      <ProgressAccordion progress={msg.progress_snapshot} />
                    </div>
                  </div>
                )}
                {/* Message bubble */}
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                        : 'max-w-full bg-white border border-black/10 text-[#374151] rounded-bl-md'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                        <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                          <button
                            onClick={() => handleCopyMessage(msg.content, idx)}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                              copiedMessageIndex === idx
                                ? 'text-[#10B981] bg-[#ECFDF5]'
                                : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                            }`}
                            title="Copy to clipboard"
                          >
                            {copiedMessageIndex === idx ? (
                              <>
                                <Check className="w-3.5 h-3.5" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ))}

                {/* Loading with structured progress */}
                {isLoading && (
              <div className="flex justify-start">
                {structuredProgress ? (
                  <div className="bg-white border border-black/10 rounded-2xl rounded-bl-md w-full max-w-2xl overflow-hidden">
                    {/* Header - Flickering dot with execution status */}
                    <div className="px-5 py-3 border-b border-black/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping flex-shrink-0" />
                          <p className="text-sm text-gray-700 font-medium truncate">
                            {(() => {
                              const steps = structuredProgress.plan_steps || [];
                              const running = steps.filter(s => s.status === 'running').length;

                              if (running > 1) {
                                return `Executing ${running} tasks in parallel${animatedDots}`;
                              }
                              if (running === 1) {
                                return `Executing${animatedDots}`;
                              }
                              // All steps completed or no steps - show backend message
                              return structuredProgress.message || 'Processing...';
                            })()}
                          </p>
                        </div>
                        {/* Percentage fixed on right - cap at 95% until task completes */}
                        {(() => {
                          const steps = structuredProgress.plan_steps || [];
                          const completed = steps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
                          const total = steps.length;
                          if (total > 0) {
                            // Cap at 95% - never show 100% until task actually completes
                            const pct = Math.min(95, Math.round((completed / total) * 100));
                            return <span className="text-sm font-semibold text-[#07E5D1] ml-3 flex-shrink-0">{pct}%</span>;
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    {/* Executing Plan - only section with progress bars */}
                    {(() => {
                      const planSteps = structuredProgress.plan_steps || [];
                      const executingComplete = planSteps.length > 0 && planSteps.every(s => s.status === 'completed' || s.status === 'skipped');
                      const completedSteps = planSteps.filter(s => s.status === 'completed' || s.status === 'skipped').length;

                      if (planSteps.length === 0) return null;

                      return (
                        <div className="px-5 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Executing Plan
                              </span>
                              {executingComplete && (
                                <CheckCircle2 className="w-3 h-3 text-[#07E5D1]" />
                              )}
                            </div>
                            <span className="text-xs text-gray-400">
                              {completedSteps}/{planSteps.length} done
                            </span>
                          </div>
                          <ul className="space-y-2 ml-1">
                            {planSteps.map((step, idx) => (
                              <li
                                key={`${step.id}-${idx}`}
                                className={`transition-all duration-300 ${
                                  step.status === 'running'
                                    ? 'bg-[#E8FCFA] rounded-lg p-2.5 -mx-1.5 border border-[#07E5D1]/20'
                                    : 'px-0.5'
                                }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="flex-shrink-0 mt-0.5">
                                    {step.status === 'completed' && (
                                      <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
                                    )}
                                    {step.status === 'running' && (
                                      <div className="relative">
                                        <Circle className="w-4 h-4 text-[#07E5D1]" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-ping" />
                                        </div>
                                      </div>
                                    )}
                                    {(step.status === 'pending' || step.status === 'skipped') && (
                                      <Circle className="w-4 h-4 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm leading-tight block ${
                                      step.status === 'completed'
                                        ? 'text-gray-500 line-through'
                                        : step.status === 'running'
                                        ? 'text-gray-800 font-medium'
                                        : 'text-gray-500'
                                    }`}>
                                      {step.label}
                                    </span>
                                    {step.status === 'running' && (
                                      <TimeBasedProgressBar
                                        taskId={step.id}
                                        startTime={stepStartTimes[step.id]}
                                        isCompleted={false}
                                      />
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                    <div className="bg-white border border-black/10 rounded-2xl rounded-bl-md px-5 py-4">
                      <div className="flex items-center gap-3 mb-1">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium text-[#374151]">{progressMessage}</span>
                  </div>
                      <p className="text-xs text-[#9CA3AF] ml-7">This may take a moment</p>
                </div>
                )}
              </div>
            )}

                <div className="h-1" />
          </div>
              )}
        </div>
      </div>

          {/* Input */}
          <div className="bg-white border-t border-[#F1F5F9] px-6 py-3">
            {queriesRemaining <= 0 ? (
              <div className="max-w-[960px] mx-auto text-center text-sm text-[#6B7280] py-2">
                You've used all your chat queries for this study.
              </div>
            ) : (
            <div className="max-w-[960px] mx-auto flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything…"
              disabled={isLoading}
                className="flex-1 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFBFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-[#9CA3AF]"
            />
            <button
              onClick={handleSend}
              aria-label="Send"
              disabled={!input.trim() || isLoading}
                className="p-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
            </div>
            )}
          </div>
        </section>
      </div>
    </MarketplaceShell>
  );
};

export default MarketplaceTwinChat;
